import { useEffect, useState } from 'react';
import { Share, SquarePlus, X } from 'lucide-react';
import Logo from '../images/logo/logo.png';
import {
  canPromptInstall,
  isHandheld,
  isIOS,
  isStandalone,
  promptInstall,
  rememberDismissed,
  subscribeInstall,
  wasDismissedRecently,
} from '../utils/installPrompt';

type Mode = 'prompt' | 'ios' | null;

function currentMode(): Mode {
  if (isStandalone() || !isHandheld() || wasDismissedRecently()) return null;
  if (canPromptInstall()) return 'prompt';
  if (isIOS()) return 'ios';
  return null;
}

/** Top bar inviting phone/tablet users to put YADAHub on their home screen. */
export default function InstallBanner() {
  const [mode, setMode] = useState<Mode>(currentMode);
  const [hidden, setHidden] = useState(false);

  useEffect(() => subscribeInstall(() => setMode(currentMode())), []);

  if (!mode || hidden) return null;

  const dismiss = () => {
    rememberDismissed();
    setHidden(true);
  };
  const install = async () => {
    const accepted = await promptInstall();
    if (!accepted) rememberDismissed();
    setHidden(true);
  };

  return (
    <div role="region" aria-label="Install YADAHub"
      className="flex items-center gap-3 border-b border-navy-3 bg-navy px-4 py-2.5 text-white">
      <img src={Logo} alt="" className="h-9 w-9 shrink-0 rounded-[10px]" />
      <div className="min-w-0 flex-1 text-[13px] leading-snug">
        <div className="font-semibold">Install YADAHub on your phone</div>
        {mode === 'prompt' ? (
          <div className="text-on-navy-muted">Opens full screen from your home screen.</div>
        ) : (
          <div className="text-on-navy-muted">
            Tap <Share aria-label="Share" className="inline h-[15px] w-[15px] -translate-y-px align-middle" /> then
            {' '}<span className="whitespace-nowrap font-semibold text-white">
              <SquarePlus aria-hidden className="inline h-[15px] w-[15px] -translate-y-px align-middle" /> Add to Home Screen
            </span>
          </div>
        )}
      </div>
      {mode === 'prompt' && (
        <button type="button" onClick={install}
          className="inline-flex min-h-[36px] shrink-0 items-center rounded-[10px] bg-accent px-3.5 text-[13px] font-semibold text-navy hover:brightness-105">
          Install
        </button>
      )}
      <button type="button" onClick={dismiss} aria-label="Not now"
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-on-navy-muted hover:bg-navy-3 hover:text-white">
        <X className="h-[18px] w-[18px]" />
      </button>
    </div>
  );
}
